import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export interface SearchController {
  query: string;
  page: number;
  setQuery: (value: string, options?: { resetPage?: boolean; replace?: boolean }) => void;
  setPage: (page: number, options?: { replace?: boolean }) => void;
  goToSearch: (value: string, options?: { replace?: boolean }) => void;
  clear: (options?: { replace?: boolean }) => void;
}

const SEARCH_ROUTE = "/search";

export const useSearchController = (): SearchController => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const query = params.get("q") ?? "";
  const rawPage = Number(params.get("page") ?? "1");
  const page = Number.isNaN(rawPage) || rawPage < 1 ? 1 : rawPage;

  const buildUrl = useCallback((nextQuery: string, nextPage: number) => {
    const nextParams = new URLSearchParams();
    if (nextQuery) {
      nextParams.set("q", nextQuery);
    }
    if (nextPage > 1) {
      nextParams.set("page", String(nextPage));
    }
    const search = nextParams.toString();
    return `${SEARCH_ROUTE}${search ? `?${search}` : ""}`;
  }, []);

  const setQuery = useCallback(
    (value: string, options?: { resetPage?: boolean; replace?: boolean }) => {
      const targetPage = options?.resetPage === false ? page : 1;
      const url = buildUrl(value, targetPage);
      const shouldReplace =
        options?.replace ?? (location.pathname === SEARCH_ROUTE && targetPage === 1);
      navigate(url, { replace: shouldReplace });
    },
    [buildUrl, navigate, page, location.pathname],
  );

  const setPage = useCallback(
    (nextPage: number, options?: { replace?: boolean }) => {
      const safePage = nextPage < 1 ? 1 : nextPage;
      const url = buildUrl(query, safePage);
      const shouldReplace = options?.replace ?? location.pathname === SEARCH_ROUTE;
      navigate(url, { replace: shouldReplace });
    },
    [buildUrl, navigate, query, location.pathname],
  );

  const goToSearch = useCallback(
    (value: string, options?: { replace?: boolean }) => {
      const url = buildUrl(value, 1);
      navigate(url, { replace: options?.replace ?? false });
    },
    [buildUrl, navigate],
  );

  const clear = useCallback(
    (options?: { replace?: boolean }) => {
      const url = buildUrl("", 1);
      navigate(url, {
        replace: options?.replace ?? location.pathname === SEARCH_ROUTE,
      });
    },
    [buildUrl, navigate, location.pathname],
  );

  return {
    query,
    page,
    setQuery,
    setPage,
    goToSearch,
    clear,
  };
};
